import React from "react";
import {
    TextField,
    NumberField,
    ComboBoxField,
    CheckField,
    LabelField,
} from "src/components/PdfInputFields/PdfInputFields";
import {
    CheckField as CheckFieldType,
    FieldTemplatesContainer,
    NumField as NumFieldType,
    ReturnDocument,
    TextField as TextFieldType,
} from "src/types";

// Function to get overlay fields based on field templates and index
export const getOverlayFields = (
    fieldTemplates: FieldTemplatesContainer[] | undefined,
    index: number,
    scale: number,
    returnDocument: ReturnDocument | undefined,
    updateReturnDocument,
    isPaymentRequest: boolean = false,
) => {
    if (!fieldTemplates || !returnDocument) return;

    // Check if the index is within valid bounds
    if (
        index < 0 ||
        index >= fieldTemplates?.length ||
        index >= (returnDocument?.pages?.length ?? 0)
    ) {
        return;
    }

    const returnDocumentPage = returnDocument.pages?.find(
        (x) => x.pageOrder === index,
    );

    const textFieldValues = returnDocumentPage?.["textFields"];
    const numberFieldValues = returnDocumentPage?.["numFields"];
    const checkFieldValues = returnDocumentPage?.["checkFields"];

    const isLocked = returnDocument?.isLocked;

    // Map check field templates to CheckField components
    const checkFieldTemplates = fieldTemplates[index]["checkFieldTemplates"]
        ?.filter((field) => field.isVisible)
        .map((field) => {
            const sanitizedValue = getSanitizedValue<CheckFieldType>(
                checkFieldValues!,
                field,
            );
            return (
                <CheckField
                    templateField={field}
                    key={field?.fieldType + field?.id + index}
                    scale={scale}
                    value={sanitizedValue}
                    updateReturnDocument={updateReturnDocument}
                    isLocked={isLocked}
                />
            );
        });

    // Map comboBox field templates to ComboBoxField components
    const comboBoxFieldTemplates = fieldTemplates[index][
        "comboBoxFieldTemplates"
    ]
        ?.filter((field) => field.isVisible)
        .map((field) => {
            const sanitizedValue = getSanitizedValue<TextFieldType>(
                textFieldValues!,
                field,
            );
            return (
                <ComboBoxField
                    templateField={field}
                    key={field?.fieldType + field?.id + index}
                    scale={scale}
                    value={sanitizedValue}
                    updateReturnDocument={updateReturnDocument}
                    isLocked={isLocked}
                />
            );
        });

    // Map label field templates to LabelField components
    const labelFieldTemplates = fieldTemplates[index]["labelFieldTemplates"]
        ?.filter((field) => field.isVisible && field.textValue)
        .map((field) => {
            return (
                <LabelField
                    field={field}
                    key={field?.fieldType + field?.id + index}
                    scale={scale}
                />
            );
        });

    // Map number field templates to NumberField components
    const numberFieldTemplates = fieldTemplates[index]["numberFieldTemplates"]
        ?.filter((templateField) => templateField.isVisible)
        .map((templateField) => {
            const sanitizedValue = getSanitizedValue<NumFieldType>(
                numberFieldValues!,
                templateField,
            );
            return (
                <NumberField
                    templateField={templateField}
                    key={templateField?.fieldType + templateField?.id + index}
                    scale={scale}
                    value={sanitizedValue}
                    updateReturnDocument={updateReturnDocument}
                    isLocked={isLocked}
                    isPaymentRequest={isPaymentRequest}
                />
            );
        });

    // Map text field templates to TextField components
    const textFieldTemplates = fieldTemplates[index]["textFieldTemplates"]
        ?.filter((templateField) => templateField.isVisible)
        .map((templateField) => {
            const sanitizedValue = getSanitizedValue<TextFieldType>(
                textFieldValues!,
                templateField,
            );
            return (
                <TextField
                    templateField={templateField}
                    key={templateField?.fieldType + templateField?.id + index}
                    scale={scale}
                    value={sanitizedValue}
                    updateReturnDocument={updateReturnDocument}
                    isLocked={isLocked}
                    isPaymentRequest={isPaymentRequest}
                />
            );
        });

    // Concatenate all field components
    return [
        ...(checkFieldTemplates || []),
        ...(comboBoxFieldTemplates || []),
        ...(labelFieldTemplates || []),
        ...(numberFieldTemplates || []),
        ...(textFieldTemplates || []),
    ];
};

type FieldType = CheckFieldType | TextFieldType | NumFieldType;

export function getSanitizedValue<T extends FieldType>(
    fieldValues: T[],
    field,
): { value: string | number | boolean; isOverride: boolean; documentField: T } {
    const fieldValue = fieldValues?.find((data) => data?.id == field?.id)!;
    let valueToUse: string | number | boolean;
    let isOverride = false;

    // Check if overrideValue exists and use it, otherwise use the default value
    if (
        fieldValue?.overrideValue !== undefined &&
        fieldValue?.overrideValue !== null &&
        fieldValue?.overrideValue !== ""
    ) {
        valueToUse = fieldValue?.overrideValue;
        isOverride = true;
    } else {
        valueToUse = fieldValue?.value ?? "";
    }
    return { value: valueToUse, isOverride, documentField: fieldValue };
}
